import {
    UserBankAccountState,
    BankProfile,
    FetchState,
    FetchStateKey,
    HsaAccountsCollection
} from "@/shared/types";
import { errorIsStatus } from "@/shared/errorHelper";
import * as storeHelper from "@/shared/storeHelper";
import * as bankService from "@/shared/api/bankService";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { templatedFetchKeyStore } from "./fetchKeyedPlugin";
import { useUserStore } from "./user";

const emptyState: UserBankAccountState = {
    fetchState: FetchState.Uninitialized,
    fetchKey: null,
    accountNumber: null,
    routingNumber: null,
};

const getEmptyHsaAccounts = () => ({ items: null });

const useHsaAccountsSubStore = defineStore("userBankAccount_hsaAccount", () => {
    const hsaAccounts = templatedFetchKeyStore<HsaAccountsCollection, any> (
        getEmptyHsaAccounts,
        (accountsState, dto) => accountsState.items = dto,
        bankService.getHsaAccounts
    );

    return {
        ...hsaAccounts
    };
});

const useHsaAccountsAdminSubStore = defineStore("userBankAccount_hsaAccount_admin", () => {
    const hsaAccounts = templatedFetchKeyStore<HsaAccountsCollection, any> (
        getEmptyHsaAccounts,
        (accountsState, dto) => accountsState.items = dto,
        bankService.getHsaAccountsAdmin
    );

    return {
        ...hsaAccounts
    };
});

export const useUserBankAccountStore = defineStore("userBankAccount", {
    state: (): UserBankAccountState => (_.cloneDeep(emptyState)),
    getters: {
        hsaAccounts () {
            const userStore = useUserStore();
            return userStore.isAdminPortal
                ? useHsaAccountsAdminSubStore().items
                : useHsaAccountsSubStore().items;
        },
        hsaAccountsLoaded () {
            const userStore = useUserStore();
            return userStore.isAdminPortal
                ? useHsaAccountsAdminSubStore().fetchState === FetchState.Loaded
                : useHsaAccountsSubStore().fetchState === FetchState.Loaded;
        },
    },
    actions: {
        invalidateLoadedState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.fetchKey)) {
                return;
            }
            const stateClone = _.cloneDeep(emptyState);
            for (const key in stateClone) {
                this[key] = stateClone[key];
            }
        },
        setLoading (fetchKey: FetchStateKey) {
            this.fetchState = FetchState.Loading;
            this.fetchKey = fetchKey;
        },
        setLoaded () {
            this.fetchState = FetchState.Loaded;
        },
        setAccount (profile: BankProfile) {
            this.accountNumber = profile.accountNumber;
            this.routingNumber = profile.routingNumber;
            this.fetchState = FetchState.Loaded;
        },
        loadBankAccountState (
            args?: { username?: string, partnerId?: number }): Promise<void> {
            args = args || {};

            const fetchStateKey: FetchStateKey = {
                username: args.username,
                partnerId: args.partnerId
            };

            this.invalidateLoadedState(fetchStateKey);
            this.setLoading(fetchStateKey);

            return fetchProfile(args)
                .then(response => {
                    if (this.fetchKey === fetchStateKey) {
                        this.setAccount(response.data);
                    }
                })
                .catch(err => {
                    if (errorIsStatus(err, 404)) {
                        this.setLoaded();
                        return Promise.resolve();
                    } else {
                        return storeHelper.handleError({ error: err, rethrow: false });
                    }
                });
        },
        loadHsaAccounts (args: { username?: string, partnerId?: number }) {
            const userStore = useUserStore();

            const hsaAccounts = userStore.isAdminPortal
                ? useHsaAccountsAdminSubStore()
                : useHsaAccountsSubStore();
            return hsaAccounts.fetchKeyedApiCall(args)
                .catch(err => storeHelper.handleError({error: err, rethrow: true}));
        },
    }
});

function fetchProfile (args?: { username?: string, partnerId?: number }) {
    const userStore = useUserStore();
    return userStore.isAdminPortal
        ? bankService.getProfileAdmin(args.username, args.partnerId)
        : bankService.getProfile(args.username, args.partnerId);
}